import { ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import { SyncLoader } from 'react-spinners'
import {
    AccountRecord,
    useCopyKeywordsToAccountMutation,
    useCopyModerationBoundariesMutation,
    useGetAccountsInTeamQuery
} from '../../api/client'
import Button from '../Button'
import Modal from '../Modal'
import { toastError } from '../Notification'
import { useState } from 'react'

type Prop = {
    account: AccountRecord
    teamId: number
    open: boolean
    setCopyModalOpen: (value: boolean) => void
}

export default function DuplicateModerationConfiguration(props: Prop) {
    const { account, teamId, open, setCopyModalOpen } = props
    // We will be copying to multiple accounts using multiple requests to the server.
    // We need to keep track of the status of copying and the current account we are copying to.
    const [copying, setCopying] = useState(false)
    const [copied, setCopied] = useState(false)
    const [currentlyCopyingTeam, setCurrentlyCopyingTeam] = useState<string>()

    // Fetch all the accounts in the target team...
    const { data: getAccountsInTeamData, loading: getAccountsInTeamLoading } =
        useGetAccountsInTeamQuery({
            variables: {
                params: { teamId: teamId }
            },
            onError: (error) => {
                toastError(error.message)
            }
        })

    const [copyModerationBoundaries] = useCopyModerationBoundariesMutation({
        onError: (error) => {
            toastError(error.message)
        }
    })

    const [copyKeywordsToAccount] = useCopyKeywordsToAccountMutation({
        onError: (error) => {
            toastError(error.message)
        }
    })

    // Copy the moderation boundaries and keywords to all the accounts in the team one by one.
    const handleCopyClick = async () => {
        setCopying(true)
        for (let targetAccount of accountsInTeam) {
            // If the team contains the source account then we want to skip it, as otherwise all the keywords will get blatted.
            if (account.id !== targetAccount.id) {
                setCurrentlyCopyingTeam(targetAccount.accountName || '')
                await copyModerationBoundaries({
                    variables: {
                        sourceAccountId: account.id,
                        targetAccountIds: [targetAccount.id]
                    }
                })

                await copyKeywordsToAccount({
                    variables: {
                        sourceAccountId: account.id,
                        targetAccountId: targetAccount.id
                    }
                })
            } else {
                console.info('Skipping copying to the source account')
            }
        }
        setCopied(true)
        setCopying(false)
    }

    const accountsInTeam = getAccountsInTeamData?.getAccountsInTeam || []

    return (
        <Modal
            open={open}
            onClose={() => setCopyModalOpen(false)}
            title="Copy moderation boundaries and keywords?"
            description="This will overwrite the existing settings for all the profiles in the selected team. It cannot be undone."
            icon={<ExclamationTriangleIcon className="h-8 w-8 text-red-600" aria-hidden="true" />}
            size="md"
            footer={
                <>
                    {copying && (
                        <div className="flex flex-row items-baseline">
                            <div className="mx-4">
                                <SyncLoader loading={true} color="#CF3C28" size={'4px'} />
                            </div>
                            <div className="ml-3">{currentlyCopyingTeam}</div>
                        </div>
                    )}
                    {copied ? (
                        <div className="flex flex-row items-baseline gap-3">
                            <div>Successfully copied moderation boundaries!</div>
                            <Button
                                primary={false}
                                onClick={() => setCopyModalOpen(false)}
                                text="Close"
                            />
                        </div>
                    ) : (
                        <>
                            {!copying && (
                                <Button
                                    primary={false}
                                    onClick={() => setCopyModalOpen(false)}
                                    text="Cancel"
                                />
                            )}
                            <Button
                                primary={true}
                                disabled={copying}
                                onClick={handleCopyClick}
                                text="Copy"
                            />
                        </>
                    )}
                </>
            }
        >
            <div className="space-y-4">
                <p>This team contains the following profiles:</p>

                {getAccountsInTeamLoading && (
                    <div className="mx-4">
                        <SyncLoader loading={true} color="#CF3C28" size={'4px'} />
                    </div>
                )}

                <ul className="max-h-60 overflow-y-auto">
                    {accountsInTeam.map((account) => {
                        return <li key={account.id}>{account.accountName}</li>
                    })}
                </ul>
            </div>
        </Modal>
    )
}
